import { useMemo } from "react";

import TerritoryLevel from "@mapmycustomers/shared/enum/TerritoryLevel";
import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import LookupData from "@mapmycustomers/shared/types/territory/LookupData";

const getSelectedCurrentLevelRegionIds = (
  selectedRegionIds: AreaRegion["id"][],
  baseLevel: TerritoryLevel,
  currentLevel: TerritoryLevel,
  lookupData: LookupData,
  includeFullInPartial: boolean = false
): [fullySelectedRegionIds: AreaRegion["id"][], partiallySelectedRegionIds: AreaRegion["id"][]] => {
  if (baseLevel === currentLevel || currentLevel === TerritoryLevel.ADM3) {
    return [selectedRegionIds, []];
  }

  const selectedRegionIdsSet = new Set(selectedRegionIds);
  const partiallySelected: AreaRegion["id"][] = [];
  const fullySelected: AreaRegion["id"][] = [];
  (Object.keys(lookupData[currentLevel]) as AreaRegion["id"][]).forEach((candidateRegionId) => {
    const candidateSubRegionIds: AreaRegion["id"][] =
      // We know that the baseLevel always exists in current lookup
      // @ts-ignore
      lookupData[currentLevel][candidateRegionId]?.[baseLevel] ?? [];

    const selectedCount = candidateSubRegionIds.reduce(
      (result, id) => result + (selectedRegionIdsSet.has(id) ? 1 : 0),
      0
    );
    if (selectedCount === candidateSubRegionIds.length) {
      fullySelected.push(candidateRegionId);
      if (includeFullInPartial) {
        partiallySelected.push(candidateRegionId);
      }
    } else if (selectedCount > 0) {
      partiallySelected.push(candidateRegionId);
    }
  });

  return [fullySelected, partiallySelected];
};

const useRegionChangesListener = (
  selectedRegionIds: AreaRegion["id"][],
  loadedRegionIds: AreaRegion["id"][],
  baseLevel: TerritoryLevel,
  currentLevel: TerritoryLevel,
  lookupData: LookupData
): [AreaRegion["id"][], AreaRegion["id"][]] => {
  const [addingRegionIds, removingRegionIds] = useMemo(() => {
    // let's find regions that were fully and partially selected BEFORE
    // and AFTER
    // Note: the before/after arrays are region ids of currentLevel, not
    // the base level

    const [beforeFull, beforePartial] = getSelectedCurrentLevelRegionIds(
      loadedRegionIds,
      baseLevel,
      currentLevel,
      lookupData,
      true // this simplifies some code
    );
    const [afterFull, afterPartial] = getSelectedCurrentLevelRegionIds(
      selectedRegionIds,
      baseLevel,
      currentLevel,
      lookupData,
      true // this simplifies some code
    );

    const beforeFullSet = new Set(beforeFull);
    const afterPartialSet = new Set(afterPartial);

    // none/partial before -> full now =>>> added
    // none before -> partial now =>>> ignored, because we can't highlight regions partially anyway,
    //      but also because user can't normally select something partially
    const addedIds = afterFull.filter((id) => !beforeFullSet.has(id));

    // partial/full before -> none now ===> removed
    const removedIds = beforePartial.filter((id) => !afterPartialSet.has(id));

    return [addedIds, removedIds];
  }, [selectedRegionIds, loadedRegionIds, baseLevel, currentLevel, lookupData]);

  return [addingRegionIds, removingRegionIds];
};

export default useRegionChangesListener;
